import {Button, Modal, TextInput} from '@/components/atoms'
import {TabsComponent} from '@/components/molecules'
import {MaxDiscountModal} from '@/components/organisms'
import {defaultConditon, usePromotion} from '@/context/Promotion/PromotionContext'
import {PromotionPreview} from '@/features/Promotions/PromotionPreview/PromotionPreview'
import {usePromotionReview} from '@/hooks/usePromotionReview'
import {PromotionType, Reward, RewardType} from '@/types/promotion'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {PromotionContionsTabList} from '@/utils/constants/promotionConstants'
import {createPromotionConditions} from '@/utils/promotions/promotions'
import {useState} from 'react'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'
import {ProductCombination} from '../components/ProductCombination'
import PromotionsConditions from '../components/PromotionsConditions'
import {ExcludeProducts} from '../components/PromotionsConditions/ExcludeProducts'
import SegmentSection from '../components/SegmentSection'
import './_promotionSetup.scss'

const PromotionsSetup = () => {
  const {
    promotionType,
    promotion: finalResult,
    onResultChange,
    maxDiscountModalVisible,
    setMaxDiscountModalVisible,
    promotionConfig,
    handleUpdatePromotionConfig,
    setPromoCondition,
    activePromoCondition,
  } = usePromotion()
  const location = useLocation()
  const {isDuplicating} = location.state || {}
  const nonEditable = finalResult?.status?.toUpperCase() === 'PUBLISHED' && !isDuplicating
  const [isPreviewModal, setPreviewModal] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  const conditions = finalResult?.promotionConditions
  const {countryCode} = useSelector((state: any) => state?.countryCode)

  const {createPreviewMessage, isMoreBtnVisible} = usePromotionReview({
    conditions,
    activePromoCondition,
    countryCode,
    promotionType,
  })

  const handleCreateLayer = () => {
    const currentSequence = conditions?.length ? conditions[conditions.length - 1].sequence : 0
    const baseRewards = conditions?.[0]?.rewards ?? []
    const baseConditions = conditions?.[0]?.conditionGroup?.conditions

    const newRewards: Reward[] = baseRewards.map(reward => {
      if (reward?.type === 'FREE_PRODUCT') {
        const rest = {...reward}
        delete rest?.quantity
        return rest
      }
      if (reward?.type === RewardType.FREE_PRODUCT_FROM_LIST) {
        return {...reward}
      }
      if (reward?.type === RewardType.DISCOUNT) {
        return {type: reward.type, discountType: reward.discountType}
      }
      if (reward?.type === RewardType.POINTS) {
        return {type: reward.type}
      }
      return {type: reward.type, items: reward.items}
    })

    const newConditionGroup =
      promotionType === PromotionType.BUY_MULTIPLE_PRODUCTS
        ? {
            sequence: currentSequence + 1,
            conditionGroup: {
              conditions: baseConditions?.length
                ? baseConditions.map(cond => {
                    const rest = {...cond}
                    delete rest.quantity
                    delete rest.amount
                    return rest
                  })
                : [defaultConditon, defaultConditon],
              ...(conditions?.[0]?.conditionGroup?.combinedWith && {
                combinedWith: conditions[0].conditionGroup.combinedWith,
              }),
            },
            rewards: newRewards,
          }
        : {
            sequence: currentSequence + 1,
            conditionGroup: {
              conditions: conditions?.[0]?.conditionGroup?.conditions?.[0]
                ? conditions[0].conditionGroup.conditions.map(condition => {
                    const rest = {...condition}
                    delete rest.quantity
                    delete rest.amount
                    delete rest.minQuantity
                    return rest
                  })
                : [defaultConditon],
            },
            rewards: newRewards,
          }

    const promotionConditions = [...(finalResult?.promotionConditions || []), newConditionGroup]
    onResultChange({
      promotionConditions,
    })
  }

  const handleResetConditions = () => {
    onResultChange({promotionConditions: createPromotionConditions(promotionType)})
    setPromoCondition(0)
    setResetKey(prev => prev + 1)
  }
  const handleSaveMaxDiscountConfig = async (newDiscount: number) => {
    try {
      await handleUpdatePromotionConfig({maxDiscountPercentage: newDiscount})
    } catch (error) {
      console.error('Error updating max discount config:', error)
      throw error
    }
  }

  const activeTab = conditions[0]?.conditionGroup?.conditions?.[0]?.items?.[0]?.uom
  const activeUOM = PromotionContionsTabList.findIndex(item => item.uom === activeTab)
  const hasFreeProductFromListReward = conditions?.[0]?.rewards?.[0]?.type === RewardType.FREE_PRODUCT_FROM_LIST

  const handleTabChange = index => {
    onResultChange({promotionConditions: createPromotionConditions(promotionType)})
    setPromoCondition(index)
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Name Promotion Section */}
      <div className="flex flex-col gap-6 rounded-lg bg-[#1D1D1D] p-6">
        <h3 className="text-lg font-semibold">Name Promotion</h3>
        <div className="w-[300px]">
          <TextInput
            label="Name*"
            onChange={(value: string) => onResultChange({name: value})}
            value={finalResult?.name || ''}
            placeholderText="Promotion Name"
            isDark={true}
            disabled={nonEditable}
          />
        </div>
      </div>

      {/* Promotion Conditions Section */}
      <div
        className={`flex flex-col gap-6 rounded-lg bg-[#1D1D1D] p-6 ${
          nonEditable ? 'pointer-events-none opacity-50' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Promotion Conditions*</h3>
          <Button
            uiSchema={{variant: 'secondary', title: 'Reset', isDark: true, size: 'small'}}
            handleButton={handleResetConditions}
          />
        </div>
        {promotionType === PromotionType.STORE_DISCOUNT || promotionType === PromotionType.WELCOME_DISCOUNT ? (
          <></>
        ) : (
          <div className="tabs-border flex items-center gap-4">
            <TabsComponent
              key={`tab-${resetKey}`}
              tabList={PromotionContionsTabList}
              defaultTabIndex={activeUOM > -1 ? activeUOM : activePromoCondition}
              onTabChange={e => handleTabChange(e)}
              className={'text-[#EFEFEF]'}
            />
          </div>
        )}

        {Array.isArray(conditions) &&
          conditions.map((condition, index) => (
            <PromotionsConditions
              key={condition?.sequence ?? index}
              index={index}
              condition={condition}
              promotionType={promotionType}
              selectedPromotionCondition={PromotionContionsTabList[activePromoCondition]?.uom}
            />
          ))}

        {!hasFreeProductFromListReward &&
          [PromotionType.BUY_SINGLE_PRODUCT, PromotionType.BUY_MULTIPLE_PRODUCTS].includes(promotionType) && (
            <Button
              uiSchema={{variant: 'tertiary', title: 'Add Layer', isDark: true, iconLeading: 'add_box', size: 'medium'}}
              handleButton={handleCreateLayer}
            />
          )}

        <ProductCombination />
        <ExcludeProducts key={JSON.stringify(finalResult?.excludedItems)} />

        <PromotionPreview
          conditions={conditions}
          activePromoCondition={activePromoCondition}
          countryCode={countryCode}
          promotionType={promotionType}
        />
      </div>

      {/* Segments Section */}
      <SegmentSection isToggleBtnDisable={countryCode === 'IND'} />

      <MaxDiscountModal
        maxDiscountValue={promotionConfig?.maxDiscountPercentage}
        showModal={maxDiscountModalVisible}
        onCloseModal={() => setMaxDiscountModalVisible(false)}
        onSaveDiscount={handleSaveMaxDiscountConfig}
      />

      <Modal
        key={'modalKey'}
        showModal={isPreviewModal}
        isDark
        title={'Preview'}
        tertiaryButtonText="Cancel"
        showTertiaryButton={true}
        classNameWrapper="min-h-80"
        content={createPreviewMessage(true)}
        type={MODAL_TYPE.SMALL}
        onClickTertiaryButton={() => setPreviewModal(!isPreviewModal)}
        hideCloseButton={true}
        primaryButtonDisabled={false}
      />
    </div>
  )
}

export default PromotionsSetup
